/**
 * The rock-tuning panel: multipliers on the measured boulder abundance,
 * tuned by eye against the orthoimage, plus its reset and copy-to-markdown
 * actions.
 */

import { ROCK_DEFAULTS, rockTuningMarkdown, type RockTuning } from '../rockField.ts';
import { paletteMarkdown } from '../rockSpecies.ts';
import type { AtlasLive } from '../../atlas';

export interface RockTuningDeps {
  readonly overlay: HTMLElement;
  readonly live: AtlasLive;
}

export interface RockTuningHandle {
  readonly rockTuning: RockTuning;
}

export function createRockTuning(deps: RockTuningDeps): RockTuningHandle {
  const { overlay } = deps;

  // ── rock tuning ─────────────────────────────────────────────────────────
  //
  // The abundance is measured; the LOOK is a judgement, and a judgement is
  // found faster by eye than by argument. These are all multipliers on the
  // measurement rather than replacements for it, so 1.00 always means "what the
  // orthoimage says", and COPY MD prints whatever you settle on in a form that
  // can be pasted straight back into a conversation.
  const ROCK_KEY = 'atlas.rocks.v1';
  const rockTuning: RockTuning = { ...ROCK_DEFAULTS };
  try {
    Object.assign(rockTuning, JSON.parse(localStorage.getItem(ROCK_KEY) ?? '{}'));
  } catch { /* defaults */ }

  const rkEls = {
    density: overlay.querySelector('#rk-density') as HTMLInputElement,
    size: overlay.querySelector('#rk-size') as HTMLInputElement,
    spread: overlay.querySelector('#rk-spread') as HTMLInputElement,
    slope: overlay.querySelector('#rk-slope') as HTMLInputElement,
    burial: overlay.querySelector('#rk-burial') as HTMLInputElement,
    budget: overlay.querySelector('#rk-budget') as HTMLInputElement,
  };
  const rkVals = {
    density: overlay.querySelector('#rk-density-v') as HTMLElement,
    size: overlay.querySelector('#rk-size-v') as HTMLElement,
    spread: overlay.querySelector('#rk-spread-v') as HTMLElement,
    slope: overlay.querySelector('#rk-slope-v') as HTMLElement,
    burial: overlay.querySelector('#rk-burial-v') as HTMLElement,
    budget: overlay.querySelector('#rk-budget-v') as HTMLElement,
  };

  function syncRockUI(): void {
    rkEls.density.value = String(Math.round(rockTuning.density * 100));
    rkEls.size.value = String(Math.round(rockTuning.size * 100));
    rkEls.spread.value = String(Math.round(rockTuning.spread * 100));
    rkEls.slope.value = String(Math.round(rockTuning.slopeDeg));
    rkEls.burial.value = String(Math.round(rockTuning.burial * 100));
    rkEls.budget.value = String(Math.round(rockTuning.budget / 1000));
    rkVals.density.textContent = `×${rockTuning.density.toFixed(2)}`;
    rkVals.size.textContent = `×${rockTuning.size.toFixed(2)}`;
    rkVals.spread.textContent = `×${rockTuning.spread.toFixed(2)}`;
    rkVals.slope.textContent = `${rockTuning.slopeDeg.toFixed(0)}°`;
    rkVals.burial.textContent = `${(rockTuning.burial * 100).toFixed(0)}%`;
    rkVals.budget.textContent = `${(rockTuning.budget / 1000).toFixed(0)}k`;
  }

  function applyRocks(): void {
    try { localStorage.setItem(ROCK_KEY, JSON.stringify(rockTuning)); } catch { /* private mode */ }
    syncRockUI();
    deps.live.plot?.setRockTuning({ ...rockTuning });
    deps.live.needsRender = true;
  }

  const rkBind: Array<[HTMLInputElement, (v: number) => void]> = [
    [rkEls.density, (v) => { rockTuning.density = v / 100; }],
    [rkEls.size, (v) => { rockTuning.size = v / 100; }],
    [rkEls.spread, (v) => { rockTuning.spread = v / 100; }],
    [rkEls.slope, (v) => { rockTuning.slopeDeg = v; }],
    [rkEls.burial, (v) => { rockTuning.burial = v / 100; }],
    [rkEls.budget, (v) => { rockTuning.budget = v * 1000; }],
  ];
  for (const [el, set] of rkBind) {
    // `change`, not `input`: each rebuild reseats up to 25k instances, and doing
    // that on every pixel of drag makes the slider feel broken.
    el.addEventListener('input', () => { set(Number(el.value)); syncRockUI(); });
    el.addEventListener('change', () => { set(Number(el.value)); applyRocks(); });
  }
  overlay.querySelector('#pc-rocks-open')?.addEventListener('click', (e) => {
    const panel = overlay.querySelector('#atlas-rockctl') as HTMLElement;
    panel.classList.toggle('hidden');
    (e.currentTarget as HTMLElement).classList.toggle('on', !panel.classList.contains('hidden'));
  });
  overlay.querySelector('#rk-reset')?.addEventListener('click', () => {
    Object.assign(rockTuning, ROCK_DEFAULTS);
    applyRocks();
  });
  overlay.querySelector('#rk-copy')?.addEventListener('click', (e) => {
    const site = deps.live.plotField?.meta.slug ?? '?';
    const rk = deps.live.plotField?.meta.rocks;
    const md = [
      `**Rock tuning** — \`${site}\``,
      rk ? `measured k = ${rk.k_used} (${rk.k_source})` : '',
      '',
      rockTuningMarkdown(rockTuning),
      '',
      '**Species palette**',
      '',
      paletteMarkdown(deps.live.plotField?.meta.slug),
    ].filter(Boolean).join('\n');
    void navigator.clipboard.writeText(md).then(() => {
      const b = e.currentTarget as HTMLElement;
      b.textContent = 'COPIED';
      setTimeout(() => { b.textContent = 'COPY MD'; }, 1200);
    });
  });
  syncRockUI();

  return { rockTuning };
}
